import { ElMessage } from "element-plus";
import type {
  BulkOutcome,
  BulkSkipInfo,
} from "~/projects/help-desk/types/bulkAssign";

const REASONS: { key: keyof BulkSkipInfo; reason: string }[] = [
  { key: "task_blocked_ids", reason: "they still have open tasks" },
  {
    key: "not_latest_ids",
    reason: "they are not the customer's latest ticket",
  },
  {
    key: "own_ticket_ids",
    reason: "they are already yours and cannot be transferred back to you",
  },
];

export interface BulkSkipGroup {
  reason: string;
  ids: number[];
}

/** The refusals a bulk action explained, grouped by why. */
export const describeBulkSkips = (
  result?: BulkSkipInfo | null,
): BulkSkipGroup[] =>
  REASONS.map(({ key, reason }) => ({
    reason,
    ids: (result?.[key] as number[] | undefined) ?? [],
  })).filter(({ ids }) => ids.length);

/**
 * Names the tickets a bulk action refused and returns their ids, so the caller
 * can keep them selected instead of quietly dropping them.
 */
const warnSkippedTickets = (result?: BulkSkipInfo | null): number[] => {
  const skipped: number[] = [];

  describeBulkSkips(result).forEach(({ reason, ids }) => {
    skipped.push(...ids);
    ElMessage.warning(
      `${ids.length} ticket(s) were skipped because ${reason}: #${ids.join(
        ", #",
      )}`,
    );
  });

  (result?.profile_errors ?? []).forEach((error) => {
    skipped.push(error.ticket_id);
    ElMessage.warning(`Ticket #${error.ticket_id}: ${error.message}`);
  });

  return [...new Set(skipped)];
};

/**
 * A bulk action answers 200 even when it changed nothing, so without this the
 * agent presses the button and sees no reply at all. Reports every refusal it
 * can explain, then the run as a whole, and returns the refused ids.
 */
export const reportBulkOutcome = (
  result: BulkOutcome | null | undefined,
  doneIds: number[],
): number[] => {
  const skipped = warnSkippedTickets(result);
  const message = result?.message;

  if (!doneIds.length) {
    ElMessage.warning(message || "None of the selected tickets were changed.");
  } else if (message && (skipped.length || result?.skipped)) {
    ElMessage.success(message);
  }

  return skipped;
};
